import React from "react";
import { Card, Tag, Typography, Tooltip, Button } from "antd";
import { useTranslation } from "react-i18next";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  TeamOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useMovil } from "../../../../hooks/useIsNarrow";
import { FilaTocable } from "../../../revi/FilaTocable";
import { CardActionsMenu } from "../CardActionsMenu";
import "./TherapyItemCard.scss";

const { Text } = Typography;

export function TherapyItemCard({ therapy, onView, onEdit, onDelete }) {
  // El titulo y la imagen los escribe el terapeuta: son contenido suyo y no se
  // traducen. Aqui solo se traduce lo que rodea a ese contenido.
  const { t } = useTranslation(["ui", "common"]);
  const { title, image, visibility, scenes } = therapy;

  // Lógica de visualización de estado
  const getStatusTag = (vis) => {
    switch (vis) {
      case "public":
        return (
          <Tag icon={<TeamOutlined />} color="blue">
            {t("visibilidad.institucion")}
          </Tag>
        );
      case "global":
        return (
          <Tag icon={<GlobalOutlined />} color="purple">
            {t("visibilidad.global")}
          </Tag>
        );
      case "private":
      default:
        return (
          <Tag icon={<UserOutlined />} color="default">
            {t("visibilidad.privada")}
          </Tag>
        );
    }
  };

  // Fallback de imagen
  const coverImage =
    image || "https://via.placeholder.com/300x150?text=Sin+Portada";

  const movil = useMovil();

  /**
   * En movil, fila simple. La portada de 150 px no aporta nada aqui: estas
   * tarjetas viven dentro del cajon de "Mis terapias", donde lo que se busca es
   * el nombre.
   *
   * Los tres iconos de 24 px de la barra pasan al menu, con el borrado al final
   * y confirmando: hoy estan pegados entre si y el de borrar a seis pixeles del
   * que se usa cada dia.
   */
  if (movil) {
    return (
      <FilaTocable
        as={Card}
        alto={64}
        className="revi-therapy-item-card revi-therapy-item-card--fila"
        onTocar={() => onView(therapy)}
        aria-label={title}
        hoverable={false}
        kebab={
          <CardActionsMenu
            ariaLabel={title}
            items={[
              {
                key: "edit",
                icon: <EditOutlined />,
                label: t("terapias.ficha.editarConfiguracion"),
                run: () => onEdit(therapy),
              },
              {
                key: "del",
                icon: <DeleteOutlined />,
                label: t("terapias.ficha.borrar"),
                peligroso: true,
                run: () => onDelete(therapy),
              },
            ]}
          />
        }
      >
        <div className="revi-therapy-item-card__filaTexto">
          <span className="revi-therapy-item-card__filaTitulo">{title}</span>
          <span className="revi-therapy-item-card__filaMeta">
            {t("common:contadores.sesiones", { count: scenes?.length || 0 })}
          </span>
        </div>
      </FilaTocable>
    );
  }

  return (
    <Card
      hoverable
      className="revi-therapy-item-card"
      cover={
        <div className="card-cover" onClick={() => onView(therapy)}>
          <img src={coverImage} alt={title} />
          <div className="status-badge">{getStatusTag(visibility)}</div>
        </div>
      }
      actions={[
        <Tooltip title={t("terapias.ficha.verDetalle")}>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onView(therapy)}
          />
        </Tooltip>,
        <Tooltip title={t("terapias.ficha.editarConfiguracion")}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(therapy)}
          />
        </Tooltip>,
        <Tooltip title={t("terapias.ficha.borrar")}>
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => onDelete(therapy)}
          />
        </Tooltip>,
      ]}
    >
      <div className="card-info" onClick={() => onView(therapy)}>
        <Text strong className="card-title" ellipsis={{ tooltip: title }}>
          {title}
        </Text>
        {/* Contador: con una sesion decia "1 sesiones". */}
        <Text type="secondary" className="card-meta">
          {t("common:contadores.sesiones", { count: scenes?.length || 0 })}
        </Text>
      </div>
    </Card>
  );
}
